/* gw: quick view — sections/gw-quick-view.liquid, styled by snippets/gw-quick-view-styles.liquid
 *
 * Any `[data-gw-quick-view="<product url>"]` button (the gw product card's bag
 * chip and hover pill) fetches the panel through the Section Rendering API and
 * shows it in ONE shell appended to <body>. A delegated listener rather than a
 * custom element (theme convention: AGENTS.md 🧩), so the triggers keep working
 * after gw-collection-grid.js swaps the grid.
 *
 * Reference build: borsali theme assets/quick-view.js — same staging on desktop:
 *   open    data-state="open"   drawer slides in, then
 *           data-gallery="open" gallery slides out from under it
 *   close   drop data-gallery    gallery slides back, then
 *           data-state="closed" drawer slides out
 * Add to cart goes through BoosterTheme.cart.addToCartJSON so Booster's own
 * post-add flow runs; the panel closes on the booster:cart:update that follows its own add.
 */
(() => {
  const DESKTOP = '(min-width: 1024px)';
  const STAGGER = 60;
  const FALLBACK = 300;

  class GwQuickView {
    constructor() {
      this.shell = null;
      this.panel = null;
      this.root = null;
      this.isOpen = false;
      this.trigger = null;
      this.variants = [];
      this.variant = null;
      this.request = 0;
      this.sequence = 0;
      this.timers = [];
      this.adding = false;
      this.onKeydown = this.onKeydown.bind(this);
      this.onCartUpdate = this.onCartUpdate.bind(this);
    }

    ensureShell() {
      if (this.shell) return;
      this.shell = document.createElement('div');
      this.shell.className = 'gw-qv-shell';
      this.shell.dataset.state = 'closed';
      this.shell.hidden = true;

      const scrim = document.createElement('div');
      scrim.className = 'gw-qv-scrim';
      scrim.addEventListener('click', () => this.close());

      this.panel = document.createElement('div');
      this.panel.className = 'gw-qv-panel';
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      this.panel.setAttribute('tabindex', '-1');
      this.panel.addEventListener('click', (event) => this.onPanelClick(event));
      this.panel.addEventListener('change', (event) => this.onPanelChange(event));

      this.shell.append(scrim, this.panel);
      document.body.appendChild(this.shell);
    }

    get isDesktop() {
      return window.matchMedia(DESKTOP).matches;
    }

    get reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    duration(name) {
      if (this.reducedMotion) return 0;
      const raw = getComputedStyle(this.shell).getPropertyValue(`--gw-qv-${name}-duration`).trim();
      const value = parseFloat(raw);
      if (Number.isNaN(value)) return FALLBACK;
      return raw.endsWith('ms') ? value : value * 1000;
    }

    after(delay, callback) {
      const token = this.sequence;
      this.timers.push(window.setTimeout(() => {
        if (this.sequence === token) callback();
      }, delay));
    }

    clearTimers() {
      this.timers.forEach(window.clearTimeout);
      this.timers = [];
    }

    /* ------------------------------------------------------------- open */

    async open(url, trigger) {
      this.ensureShell();
      this.clearTimers();
      this.sequence += 1;
      this.trigger = trigger || null;

      const loading = document.createElement('div');
      loading.className = 'gw-qv-panel__loading';
      loading.textContent = 'Loading…';
      this.panel.replaceChildren(loading);
      this.shell.removeAttribute('data-gallery');
      this.shell.hidden = false;
      // flush styles so the slide-in has a start value (rAF never fires in a background tab)
      void this.shell.offsetWidth;
      this.shell.dataset.state = 'open';
      this.isOpen = true;
      this.openedAt = performance.now();

      document.documentElement.classList.add('gw-qv-locked');
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('booster:cart:update', this.onCartUpdate);

      const token = ++this.request;
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', 'gw-quick-view');

      try {
        const response = await fetch(fetchUrl.toString());
        if (!response.ok) throw new Error(`gw-quick-view: ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (token !== this.request || !this.isOpen) return;
        const content = doc.querySelector('[data-gw-qv]');
        if (!content) throw new Error('gw-quick-view: panel missing in response');
        this.panel.replaceChildren(document.importNode(content, true));
        this.afterRender();
      } catch (error) {
        console.error(error);
        if (token === this.request) window.location.href = url;
      }
    }

    afterRender() {
      this.root = this.panel.querySelector('[data-gw-qv]');
      if (!this.root) return;
      const data = this.root.querySelector('[data-gw-qv-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];

      const title = this.root.querySelector('.gw-qv__title');
      if (title) this.panel.setAttribute('aria-label', title.textContent.trim());

      const atc = this.root.querySelector('[data-gw-qv-atc]');
      // a direct listener: Booster reads event.currentTarget as the button it disables and relabels
      if (atc) atc.addEventListener('click', (event) => this.addToCart(event));

      this.readSelection();
      this.trapFocus();
      this.revealGallery();
      if (typeof BoosterTheme !== 'undefined' && BoosterTheme.events) {
        BoosterTheme.events.trigger('booster:content:update', { trigger: 'gw-quick-view' });
      }
    }

    revealGallery() {
      const show = () => this.shell.setAttribute('data-gallery', 'open');
      if (this.reducedMotion || !this.isDesktop) {
        show();
        return;
      }
      void this.panel.offsetWidth;
      const elapsed = performance.now() - this.openedAt;
      this.after(Math.max(STAGGER, this.duration('drawer') + STAGGER - elapsed), show);
    }

    /* ------------------------------------------------------------- events */

    onPanelClick(event) {
      if (event.target.closest('[data-gw-qv-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      const step = event.target.closest('[data-gw-qv-step]');
      if (step) {
        event.preventDefault();
        const input = this.root && this.root.querySelector('[data-gw-qv-quantity]');
        if (!input) return;
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(step.dataset.gwQvStep, 10));
      }
    }

    onPanelChange(event) {
      if (event.target.matches('.gw-qv__radio')) this.readSelection();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        event.stopPropagation();
        this.close();
      }
    }

    onCartUpdate() {
      // Booster also fires this on its own cart syncs (page load, other tabs): close only after our add
      if (this.isOpen && this.adding) this.close();
    }

    /* ------------------------------------------------------------- selection */

    readSelection() {
      if (!this.root) return;
      const groups = Array.from(this.root.querySelectorAll('[data-gw-qv-option]'));
      const selected = groups.map((group) => {
        const radio = group.querySelector('.gw-qv__radio:checked');
        return radio ? radio.value : null;
      });

      this.variant = groups.length
        ? this.variants.find((variant) => variant.options.every((option, index) => option === selected[index]))
        : this.variants[0];

      groups.forEach((group, index) => {
        const label = group.querySelector('[data-gw-qv-option-value]');
        if (label) label.textContent = selected[index] || '';
        // a value is "unavailable" when no in-stock variant pairs it with the other current choices
        group.querySelectorAll('.gw-qv__radio').forEach((radio) => {
          const available = this.variants.some((variant) => variant.available
            && variant.options[index] === radio.value
            && variant.options.every((option, other) => other === index || option === selected[other]));
          radio.classList.toggle('is-unavailable', !available);
        });
      });

      const extras = groups.map((group, index) => {
        const name = group.querySelector('.gw-qv__option-name');
        return name && /size/i.test(name.textContent) ? null : selected[index];
      }).filter(Boolean);
      const line = this.root.querySelector('[data-gw-qv-variant-line]');
      if (line) line.textContent = extras.join(' / ');

      this.renderVariant();
    }

    renderVariant() {
      const atc = this.root.querySelector('[data-gw-qv-atc]');
      const text = atc && atc.querySelector('[data-button-text]');
      if (!this.variant) {
        if (atc) atc.disabled = true;
        if (text) text.textContent = atc.dataset.soldOutLabel;
        return;
      }

      const price = this.root.querySelector('[data-gw-qv-price]');
      if (price) {
        const current = document.createElement('span');
        current.className = 'gw-qv__price-current';
        current.textContent = this.variant.price;
        price.replaceChildren(current);
        if (this.variant.compare_at_price) {
          const compare = document.createElement('s');
          compare.className = 'gw-qv__price-compare';
          compare.textContent = this.variant.compare_at_price;
          price.append(compare);
        }
      }

      const stock = this.root.querySelector('[data-gw-qv-stock]');
      if (stock) stock.textContent = this.variant.available ? stock.dataset.inStock : stock.dataset.outOfStock;
      const atcPrice = this.root.querySelector('[data-gw-qv-atc-price]');
      if (atcPrice) atcPrice.textContent = this.variant.available ? ` — ${this.variant.price}` : '';

      if (atc) {
        atc.dataset.variantId = this.variant.id;
        atc.disabled = !this.variant.available;
        if (text) text.textContent = this.variant.available ? atc.dataset.addLabel : atc.dataset.soldOutLabel;
      }

      if (this.variant.media_id) {
        const target = this.root.querySelector(`[data-gw-qv-media-id="${this.variant.media_id}"]`);
        const media = this.root.querySelector('.gw-qv__media');
        if (target && media) media.prepend(target);
      }
    }

    addToCart(event) {
      if (!this.variant || !this.variant.available) return;
      const input = this.root.querySelector('[data-gw-qv-quantity]');
      const quantity = Math.max(1, parseInt(input && input.value, 10) || 1);
      const items = [{ id: this.variant.id, quantity }];
      this.adding = true;
      if (typeof BoosterTheme !== 'undefined' && BoosterTheme.cart) {
        BoosterTheme.cart.addToCartJSON(event, { items });
        return;
      }
      fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      }).then(() => { window.location.href = '/cart'; });
    }

    /* ------------------------------------------------------------- focus */

    trapFocus() {
      const focusable = this.panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const close = this.panel.querySelector('[data-gw-qv-close]');
      (close || focusable[0] || this.panel).focus();
      this.first = focusable[0];
      this.last = focusable[focusable.length - 1];
      if (this.handleTab) document.removeEventListener('keydown', this.handleTab);
      this.handleTab = (event) => {
        if (event.key !== 'Tab' || !this.first) return;
        if (event.shiftKey && document.activeElement === this.first) {
          event.preventDefault();
          this.last.focus();
        } else if (!event.shiftKey && document.activeElement === this.last) {
          event.preventDefault();
          this.first.focus();
        }
      };
      document.addEventListener('keydown', this.handleTab);
    }

    /* ------------------------------------------------------------- close */

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.adding = false;
      this.request += 1;
      this.clearTimers();
      this.sequence += 1;

      document.documentElement.classList.remove('gw-qv-locked');
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('booster:cart:update', this.onCartUpdate);
      if (this.handleTab) document.removeEventListener('keydown', this.handleTab);

      const drawer = this.duration('drawer');
      const finish = () => {
        if (this.isOpen) return;
        this.shell.hidden = true;
        this.shell.removeAttribute('data-gallery');
        this.panel.replaceChildren();
        this.root = null;
      };
      const closeDrawer = () => {
        this.shell.dataset.state = 'closed';
        if (drawer) this.after(drawer, finish);
        else finish();
      };

      const gallery = this.isDesktop ? this.duration('gallery') : 0;
      if (gallery && this.shell.hasAttribute('data-gallery')) {
        this.shell.removeAttribute('data-gallery');
        this.after(gallery, closeDrawer);
      } else {
        this.shell.removeAttribute('data-gallery');
        closeDrawer();
      }

      if (this.trigger && document.contains(this.trigger)) this.trigger.focus();
      this.trigger = null;
    }
  }

  const quickView = new GwQuickView();

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-gw-quick-view]');
    if (!trigger || trigger.disabled) return;
    event.preventDefault();
    quickView.open(trigger.dataset.gwQuickView, trigger);
  });
})();
